/**
 * FIXERLIGHT-052 lot 1 — le statut d'un rendez-vous d'atelier
 * (`requested`/`confirmed`/`done`/`cancelled`) est une valeur BRUTE côté api
 * (contrat `docs/specs/fixerlight-052-mes-rendez-vous-atelier.md` § 4, repo
 * api : « le mot client... est un mapping du FRONT, même doctrine que
 * slots/full_slots »). Ce module est ce mapping : jamais de traduction brute
 * du statut technique dans un template.
 */

const STATUS_I18N_KEYS = {
  requested: 'workshopappointments.status.requested',
  confirmed: 'workshopappointments.status.confirmed',
  done: 'workshopappointments.status.done',
  cancelled: 'workshopappointments.status.cancelled',
};

// Groupe de couleur du thème (voir src/styles/theme.jsx) consommé par
// l'atome <Badge color=... />.
const STATUS_BADGE_COLORS = {
  requested: 'yellow',
  confirmed: 'blue',
  done: 'green',
  cancelled: 'gray',
};

/** Clé i18n du statut, ou null si le statut n'est pas reconnu (fail-open : ne
 * jamais planter sur une valeur inconnue). */
export function workshopBookingStatusKey(status) {
  return STATUS_I18N_KEYS[status] || null;
}

/** Groupe de couleur Badge pour ce statut ; gris si non reconnu. */
export function workshopBookingStatusColor(status) {
  return STATUS_BADGE_COLORS[status] || 'gray';
}

/**
 * Un rendez-vous `requested` n'a pas d'heure arrêtée : `scheduled_at` n'y est
 * que le pivot de tri (créneau proposé le plus tôt), jamais une heure
 * retenue (contrat § 4). Les autres statuts affichent `scheduled_at`
 * directement.
 */
export function isPendingWorkshopBooking(status) {
  return status === 'requested';
}

/**
 * FIXERLIGHT-148 — un rendez-vous `done` ou `cancelled` est clos : il est
 * « passé » quelle que soit sa date prévue (un atelier peut terminer un
 * rendez-vous avant l'heure). Même liste que `Booking::CLOSED_STATUSES`
 * côté api, qui construit l'ordre de la liste avec cette règle.
 */
export function isClosedWorkshopBooking(status) {
  return status === 'done' || status === 'cancelled';
}

/**
 * Annuler (contrat § 5, lot 2) : possible depuis `requested` (une demande
 * pas encore tranchée) et `confirmed`, jamais depuis `done`/`cancelled` —
 * même machine à états que `Booking::canTransitionTo()` côté api.
 */
export function canCancelWorkshopBooking(status) {
  return status === 'requested' || status === 'confirmed';
}

/**
 * Déplacer (contrat § 6, lot 2) : seulement depuis `confirmed` — une
 * demande `requested` a déjà plusieurs créneaux PROPOSÉS, en changer un
 * n'a pas de sens ici (l'annuler puis reposer une nouvelle demande, hors
 * périmètre de ce lot).
 */
export function canRescheduleWorkshopBooking(status) {
  return status === 'confirmed';
}

// Le `code` machine d'un refus (`WorkshopBookingActionError`, contrat § 5/§
// 6/§ 8 — `ClientWorkshopBookingController`) mappé sur une phrase claire.
// `booking_not_cancellable` (cancel) et `booking_not_confirmed` (reschedule)
// disent tous deux la même chose au client : la transition demandée n'est
// plus possible, l'écran doit se rafraîchir sur l'état réel.
const ACTION_ERROR_I18N_KEYS = {
  booking_too_close: 'workshopappointments.detail.error.tooClose',
  slot_not_available: 'workshopappointments.detail.error.slotNotAvailable',
  booking_not_cancellable:
    'workshopappointments.detail.error.transitionRefused',
  booking_not_confirmed: 'workshopappointments.detail.error.transitionRefused',
  // FIX-1155 : plusieurs horaires proposés à un atelier qui réserve en direct
  // (passé instantané entre l'ouverture du panneau et l'envoi).
  single_slot_required: 'workshopappointments.detail.error.singleSlotRequired',
};

/** Clé i18n pour un `WorkshopBookingActionError.code` ; générique si absent
 * ou non reconnu (panne réseau, réponse illisible). */
export function workshopBookingActionErrorKey(code) {
  return (
    ACTION_ERROR_I18N_KEYS[code] || 'workshopappointments.detail.error.generic'
  );
}
